import compose from 'lodash/fp/compose'
import get from 'lodash/fp/get'
import at from 'lodash/fp/at'
import { take, call, put } from 'redux-saga/effects'
import { HACIENDA_BLOCKED_REQUEST } from 'inbonis-exception/lib'
import { ApiError } from '../../services/request/errors'
import { createAuthedApiSaga, createRoutineSaga, SAGA_WATCHER_LATEST_MODE } from '../../utils/saga'
import injectSaga from '../../state/hoc/injectSaga'

import {
  fetchAllApplysRoutine,
  fetchApplyByIdRoutine,
  applyStepRoutine,
  requestInfoRoutine,
  fetchRequiredDocumentsRoutine,
  checkDocumentsRoutine,
  createHaciendaRoutine,
  uploadFilesRoutine,
  createApplyRoutine,
  haciendaBlockedAction
} from './routines'
import getApplicationsApi from './api'

// callers
export function * getAuthedApi() {
  const api = yield * createAuthedApiSaga(getApplicationsApi)
  return api
}

export function * fetchAllApplysSaga(userId) {
  const api = yield * getAuthedApi()
  const result = yield call(api.allByUserId, userId)

  return result
}

export function * fetchApplyByIdSaga(id) {
  const api = yield * getAuthedApi()
  const result = yield call(api.getById, id)

  return result
}

export function * applyStepSaga(id, data) {
  const api = yield * getAuthedApi()
  const result = yield call(api.step, id, data)

  return result
}

export function * createApplySaga(data) {
  const api = yield getAuthedApi()
  const result = yield call(api.create, data)

  return result
}

export function * fetchRequiredDocumentsSaga(id) {
  const api = yield * getAuthedApi()
  const result = yield call(api.getRequiredDocuments, id)

  return result
}

export function * requestInfoSaga(id, data) {
  const api = yield * getAuthedApi()
  const result = yield call(api.requestInfo, id, data)

  return result
}

export function * checkDocumentsSaga(id) {
  const api = yield * getAuthedApi()
  const result = yield call(api.checkDocuments, id)

  return result
}

export function * createHaciendaSaga(id, data) {
  const api = yield * getAuthedApi()
  const result = yield call(api.postHacienda, id, data)

  return result
}

export function * uploadFilesSaga(id, data) {
  const api = yield * getAuthedApi()
  const result = yield call(api.postFile, id, data)

  return result
}

// watchers
export const { watcher: fetchAllApplysSagaWatcher } = createRoutineSaga({
  routine: fetchAllApplysRoutine,
  requestSaga: fetchAllApplysSaga,
  requestArgsCreator: get('payload'),
  watcherMode: SAGA_WATCHER_LATEST_MODE
})

export const { watcher: fetchApplyByIdSagaWatcher } = createRoutineSaga({
  routine: fetchApplyByIdRoutine,
  requestSaga: fetchApplyByIdSaga,
  requestArgsCreator: get('payload'),
  watcherMode: SAGA_WATCHER_LATEST_MODE
})

export const { watcher: applyStepSagaWatcher } = createRoutineSaga({
  routine: applyStepRoutine,
  requestSaga: applyStepSaga,
  requestArgsCreator: at(['payload.id', 'payload']),
  watcherMode: SAGA_WATCHER_LATEST_MODE
})

export const { watcher: createApplyWatcher } = createRoutineSaga({
  routine: createApplyRoutine,
  requestSaga: createApplySaga,
  requestArgsCreator: get('payload'),
  watcherMode: SAGA_WATCHER_LATEST_MODE
})

export const { watcher: requestInfoSagaWatcher } = createRoutineSaga({
  routine: requestInfoRoutine,
  requestSaga: requestInfoSaga,
  requestArgsCreator: at(['payload.id', 'payload']),
  watcherMode: SAGA_WATCHER_LATEST_MODE
})

export const { watcher: fetchRequiredDocumentsSagaWatcher } = createRoutineSaga({
  routine: fetchRequiredDocumentsRoutine,
  requestSaga: fetchRequiredDocumentsSaga,
  requestArgsCreator: get('payload'),
  watcherMode: SAGA_WATCHER_LATEST_MODE
})

export const { watcher: checkDocumentsSagaWatcher } = createRoutineSaga({
  routine: checkDocumentsRoutine,
  requestSaga: checkDocumentsSaga,
  requestArgsCreator: get('payload'),
  watcherMode: SAGA_WATCHER_LATEST_MODE
})

export const { watcher: createHaciendaSagaWatcher } = createRoutineSaga({
  routine: createHaciendaRoutine,
  requestSaga: createHaciendaSaga,
  requestArgsCreator: at(['payload.id', 'payload']),
  watcherMode: SAGA_WATCHER_LATEST_MODE
})

export const { watcher: uploadFilesSagaWatcher } = createRoutineSaga({
  routine: uploadFilesRoutine,
  requestSaga: uploadFilesSaga,
  requestArgsCreator: at(['payload.id', 'payload.data']),
  watcherMode: SAGA_WATCHER_LATEST_MODE
})

export function * haciendaFailWatcher() {
  while(true) {
    const { payload: error } = yield take(createHaciendaRoutine.FAILURE)

    if (
      error.is &&
      error.is(ApiError) &&
      error.errorCode === HACIENDA_BLOCKED_REQUEST
    ) {
      yield put(haciendaBlockedAction())
    }
  }
}

// injectors
export const withFetchAllApplysSaga = injectSaga({
  key: 'applys/watchAll',
  saga: fetchAllApplysSagaWatcher
})

export const withFetchApplyByIdSaga = injectSaga({
  key: 'applys/byId',
  saga: fetchApplyByIdSagaWatcher
})

export const withApplyStepSaga = injectSaga({
  key: 'applys/step',
  saga: applyStepSagaWatcher
})

export const withCreateApplySaga = injectSaga({
  key: 'applys/create',
  saga: createApplyWatcher
})

export const withRequestInfoSaga = injectSaga({
  key: 'applys/requesInfo',
  saga: requestInfoSagaWatcher
})

export const withFetchRequiredDocumentsSaga = injectSaga({
  key: 'applys/requiredDocuments',
  saga: fetchRequiredDocumentsSagaWatcher
})

export const withCheckDocumentsSaga = injectSaga({
  key: 'applys/checkDocuments',
  saga: checkDocumentsSagaWatcher
})

export const withCreateHaciendasSaga = compose(
  injectSaga({
    key: 'applys/uploadHacienda',
    saga: createHaciendaSagaWatcher
  }),
  injectSaga({
    key: 'applys/haciendaFail',
    saga: haciendaFailWatcher
  })
)

export const withUploadFilesSaga = injectSaga({
  key: 'applys/uploadFIle',
  saga: uploadFilesSagaWatcher
})
